import React, {HTMLAttributes} from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';

/**
 * Wrapper component for convenient use of ReactFlow, which requires a parent
 * container with fixed-value height and width properties. By default, the wrapper
 * will assume the full height of its parent container. The wrapper takes in a
 * required width argument. If fullWidth is true, the wrapper will ignore the width
 * argument and assume the full width of its parent container.
 *
 * This component makes a div which fills its parent, then calculates its pixel
 * dimensions to provide the type of div that ReactFlow needs. It accepts a child
 * that is either a function or a ReactNode. If it's a function, then it can pass
 * the X position of the <Controls/> to the child for rendering. Note that the X
 * position will only be defined if containerWidthForControls is specified. The
 * containerWidthForControls is the width of the container for ReactFlowWrapper
 * (i.e. the parent component of ReactFlowWrapper).
 */
export default function ReactFlowWrapper(
  props: HTMLAttributes<HTMLDivElement> & {
    width?: number;
    children: React.ReactNode | ((reactFlowControlsXPosition: number | null) => React.ReactNode);
    className?: string;
    fullWidth?: boolean;
    containerWidthForControls?: number;
  }
): JSX.Element {
  const {ref, height} = useResizeObserver();
  assert(props.fullWidth || props.width != null, 'width or fullWidth must be defined');
  const {width, children, className, fullWidth, containerWidthForControls, ...divProps} = props;
  const actualWidth = fullWidth ? '100%' : width;
  const reactFlowControlsXPosition =
    _.isNumber(containerWidthForControls) && _.isNumber(width)
      ? (containerWidthForControls - width) / 2
      : null;
  const actualChildren = _.isFunction(children) ? children(reactFlowControlsXPosition) : children;

  return (
    <div
      ref={ref}
      {...divProps}
      className={className}
      style={{width: actualWidth, height: '100%', margin: '0 auto'}}
    >
      <div style={{width, height}}>{actualChildren}</div>
    </div>
  );
}
